import sodium from "libsodium-wrappers-sumo";

import {
  ARGON2_MEMORY_BYTES,
  ARGON2_OPS_LIMIT,
  KEY_BYTES,
} from "../codec/constants";
import { concatBytes, utf8 } from "../codec/bytes";

let readyPromise: Promise<typeof sodium> | undefined;
let keyWorker: Worker | undefined;
let keyRequestId = 0;
const pendingKeys = new Map<number, { resolve: (key: Uint8Array) => void; reject: (error: Error) => void }>();

export function sodiumReady(): Promise<typeof sodium> {
  readyPromise ??= sodium.ready.then(() => sodium);
  return readyPromise;
}

function corpusSalt(corpusId: string, saltBytes: number): Uint8Array {
  const id = utf8(corpusId);
  if (id.length > saltBytes) throw new RangeError(`Corpus id exceeds ${saltBytes} UTF-8 bytes.`);
  const salt = new Uint8Array(saltBytes);
  salt.set(id);
  return salt;
}

export async function deriveKeyDirect(username: string, secret: string, corpusId: string): Promise<Uint8Array> {
  const lib = await sodiumReady();
  const password = concatBytes(
    utf8(username.normalize("NFC")),
    new Uint8Array([0]),
    utf8(secret.normalize("NFC")),
  );
  return lib.crypto_pwhash(
    KEY_BYTES,
    password,
    corpusSalt(corpusId, lib.crypto_pwhash_SALTBYTES),
    ARGON2_OPS_LIMIT,
    ARGON2_MEMORY_BYTES,
    lib.crypto_pwhash_ALG_ARGON2ID13,
  );
}

function getKeyWorker(): Worker {
  if (keyWorker) return keyWorker;
  keyWorker = new Worker(new URL("./kdf.worker.ts", import.meta.url), { type: "module", name: "poem-codec-kdf" });
  keyWorker.addEventListener("message", (event: MessageEvent<{ id: number; key?: Uint8Array; error?: string }>) => {
    const pending = pendingKeys.get(event.data.id);
    if (!pending) return;
    pendingKeys.delete(event.data.id);
    if (event.data.key) pending.resolve(new Uint8Array(event.data.key));
    else pending.reject(new Error(event.data.error ?? "Key derivation failed."));
  });
  keyWorker.addEventListener("error", (event) => {
    const error = new Error(event.message || "Key derivation worker failed.");
    for (const pending of pendingKeys.values()) pending.reject(error);
    pendingKeys.clear();
    keyWorker?.terminate();
    keyWorker = undefined;
  });
  return keyWorker;
}

export function deriveKey(username: string, secret: string, corpusId: string): Promise<Uint8Array> {
  if (typeof Worker === "undefined") return deriveKeyDirect(username, secret, corpusId);
  const id = ++keyRequestId;
  return new Promise((resolve, reject) => {
    pendingKeys.set(id, { resolve, reject });
    getKeyWorker().postMessage({ id, username, secret, corpusId });
  });
}
